/**
 * Owns the run state machine, the timer, the splits and the personal best.
 *
 * Keeps no three.js and no DOM references, so the whole "what counts as a run"
 * question is testable in isolation.
 */

import { runCategoryKey } from '../score/runCategory';
import { createSeededLevel } from '../sim/SeededLevel';
import { simulationFingerprint } from '../sim/fingerprint';
import type { IntoxicationLevel } from '../config/intoxication';
import type { Vec3 } from '../utils/math';
import { RunState } from './GameState';
import { RunTimer } from './Timer';
import { CheckpointTracker, type Split } from '../world/Checkpoint';
import type { CheckpointId, CheckpointSpec } from '../world/Level';
import {
  comparePersonalBest,
  type PersonalBestComparison,
  type RunResult,
  type ScoreRepository,
} from '../score/ScoreRepository';

export interface FinishedRun {
  result: RunResult;
  comparison: PersonalBestComparison;
}

export interface RunManagerOptions {
  /** Fixed layout identity; defaults to the original layout of this build. */
  seed?: number;
  fingerprint?: string;
  checkpoints: readonly CheckpointSpec[];
  repository: ScoreRepository;
  playerName: string;
  levelName: string;
  /** The dose this run is set at. Part of the run's identity. */
  intoxication: IntoxicationLevel;
  /** Injectable so tests are not at the mercy of the wall clock. */
  now?: () => number;
  /**
   * Anything the submission pipeline needs stamped onto the result: which
   * server-issued run id the attempt was played under, and how much replay was
   * captured. Optional, because a run works perfectly well with neither.
   */
  runMetadata?: () => { runId?: string; replayFrames?: number };
}

export class RunManager {
  readonly timer = new RunTimer();
  readonly checkpoints: CheckpointTracker;

  private currentState: RunState = RunState.Ready;
  private stateBeforePause: RunState = RunState.Ready;
  private personalBestRun: RunResult | null = null;
  private lastFinishedRun: FinishedRun | null = null;
  private intoxication: IntoxicationLevel;
  private readonly now: () => number;
  private readonly seed: number;
  private readonly fingerprint: string;
  /** Hannu landing a swipe is recorded per run, purely as metadata. */
  private hannuHits = 0;

  constructor(private readonly options: RunManagerOptions) {
    this.seed = options.seed ?? 0;
    this.fingerprint = options.fingerprint ?? simulationFingerprint(createSeededLevel(this.seed));
    this.checkpoints = new CheckpointTracker(options.checkpoints);
    this.intoxication = options.intoxication;
    this.now = options.now ?? (() => Date.now());
  }

  /** Loads the stored PB for the current dose. Safe to call before frame 1. */
  async init(): Promise<void> {
    this.personalBestRun = await this.options.repository.loadPersonalBest(this.category);
  }

  get category(): string {
    return runCategoryKey(this.intoxication, this.seed, this.fingerprint);
  }

  get intoxicationLevel(): IntoxicationLevel {
    return this.intoxication;
  }

  /**
   * Changing the dose changes the game, so it also changes which leaderboard
   * you are on. The caller is responsible for restarting the run.
   */
  async setIntoxication(level: IntoxicationLevel): Promise<void> {
    this.intoxication = level;
    this.personalBestRun = await this.options.repository.loadPersonalBest(this.category);
  }

  get hannuHitCount(): number {
    return this.hannuHits;
  }

  recordHannuHit(): void {
    if (this.currentState !== RunState.Running) return;
    this.hannuHits++;
  }

  get state(): RunState {
    return this.currentState;
  }

  get personalBest(): RunResult | null {
    return this.personalBestRun;
  }

  get lastRun(): FinishedRun | null {
    return this.lastFinishedRun;
  }

  get elapsedMs(): number {
    return this.timer.milliseconds;
  }

  get simulationTime(): number {
    return this.timer.seconds;
  }

  /**
   * Steps completed so far. `simulationTime` is this divided by SIMULATION_HZ,
   * but anything that needs to *seed* something wants the integer - a float
   * that happens to land on the same value is not the same guarantee.
   */
  get stepCount(): number {
    return this.timer.stepCount;
  }

  /**
   * Full reset: `simulationTime` goes back to exactly zero.
   *
   * `keepPaused` matters when the restart was triggered from inside the pause
   * menu - changing the dose restarts the run, and having the settings panel
   * vanish out from under the slider you are dragging is miserable.
   */
  reset(keepPaused = false): void {
    const wasPaused = this.currentState === RunState.Paused;
    this.timer.reset();
    this.checkpoints.reset();
    this.currentState = keepPaused && wasPaused ? RunState.Paused : RunState.Ready;
    this.stateBeforePause = RunState.Ready;
    this.lastFinishedRun = null;
    this.hannuHits = 0;
  }

  /** READY -> RUNNING. Triggered by the first gameplay input. */
  start(): boolean {
    if (this.currentState !== RunState.Ready) return false;
    this.currentState = RunState.Running;
    return true;
  }

  pause(): boolean {
    if (this.currentState === RunState.Paused) return false;
    this.stateBeforePause = this.currentState;
    this.currentState = RunState.Paused;
    return true;
  }

  resume(): boolean {
    if (this.currentState !== RunState.Paused) return false;
    this.currentState = this.stateBeforePause;
    return true;
  }

  /** Advances the run clock by exactly one fixed step. */
  advanceTimer(): void {
    if (this.currentState !== RunState.Running) return;
    this.timer.advance();
  }

  /** Feeds the player position to the checkpoint tracker. */
  updateCheckpoints(feet: Vec3): Split[] {
    if (this.currentState !== RunState.Running) return [];
    return this.checkpoints.update(feet, this.timer.milliseconds);
  }

  /** Records an action at the same step boundary as the positional splits. */
  completeCheckpoint(id: CheckpointId): Split[] {
    if (this.currentState !== RunState.Running) return [];
    return this.checkpoints.complete(id, this.timer.milliseconds);
  }

  /**
   * RUNNING -> FINISHED. Records the PIN split, compares against the PB and
   * persists it if it is faster.
   */
  finish(): FinishedRun | null {
    if (this.currentState !== RunState.Running) return null;
    const timeMs = this.timer.milliseconds;
    this.checkpoints.complete('PIN_CODE', timeMs);
    this.currentState = RunState.Finished;

    const result: RunResult = {
      seed: this.seed,
      fingerprint: this.fingerprint,
      timeMs,
      splits: [...this.checkpoints.reached],
      playerName: this.options.playerName,
      levelName: this.options.levelName,
      intoxication: this.intoxication,
      category: this.category,
      hannuHits: this.hannuHits,
      recordedAt: this.now(),
      ...this.options.runMetadata?.(),
    };
    const comparison = comparePersonalBest(this.personalBestRun, result);
    if (comparison.isPersonalBest) {
      this.personalBestRun = result;
      void this.options.repository.savePersonalBest(result);
    }

    this.lastFinishedRun = { result, comparison };
    return this.lastFinishedRun;
  }
}
